import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { TaxonomyGapReview } from "@shared/types/corpus/api.ts";
import type { TaxonomyGapEntry } from "@shared/types/taxonomy.ts";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../components/primitives/data-display/DataSequence";
import { Pill } from "../../../../components/primitives/data-display/Pill";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { gaps as spacing } from "../../../../components/primitives/spacing";
import { useAsyncHandler } from "../../../../hooks/useAsyncHandler";
import { useDialogState } from "../../../../hooks/useDialogState";
import { decideTaxonomyGap } from "../../../../lib/corpusApi";
import { LibraryPage } from "../LibraryPage";
import { DecideGapDialog } from "./DecideGapDialog";
import { describeGapEntry } from "./gapEntryDraft";
import { GAP_STATUS_FILTERS, type TaxonomyGapFilters } from "./taxonomyGapFilters";
import { useTaxonomyGaps } from "./useTaxonomyGaps";

const STATUS_LABELS: Record<TaxonomyGapFilters["status"], string> = {
  all: "All",
  escalated: "Escalated",
  open: "Open",
  decided: "Decided",
};

const KIND_LABELS: Record<TaxonomyGapFilters["kind"], string> = {
  all: "All",
  journal: "Journals",
  funding_agency: "Funders",
  section_title: "Section titles",
  mesh_tree_prefix: "MeSH prefixes",
};

const STATUS_TONES: Record<TaxonomyGapReview["status"], "muted" | "accent" | "warning"> = {
  open: "muted",
  escalated: "warning",
  decided: "accent",
};

interface ReopenButtonProps {
  gap: TaxonomyGapReview;
  onReopen: (id: string) => Promise<void>;
}

/** Sends one gap back to the judge. */
function ReopenButton({ gap, onReopen }: ReopenButtonProps) {
  const { loading, error, handle } = useAsyncHandler(() => onReopen(gap.id));
  return (
    <Button size="small" color={error ? "error" : "inherit"} disabled={loading} onClick={handle}>
      {error ? "Failed, retry" : "Re-judge"}
    </Button>
  );
}

/** The vocabulary entries documents lacked and that are not yet filled: the escalated ones wait
 *  for a person; a person can also settle, or overrule, any of the others. */
export default function TaxonomyGaps() {
  const { gaps, loading, error, pagination, filters, setFilters, refresh, reopen } =
    useTaxonomyGaps();
  const decision = useDialogState<TaxonomyGapReview>();

  const columns: DataSequenceColumn<TaxonomyGapReview>[] = [
    {
      key: "value",
      header: "Entry",
      width: 280,
      cardRole: "primary",
      cell: (gap) => (
        <Stack>
          <Typography variant="body2">{gap.context?.label ?? gap.value}</Typography>
          <Typography variant="caption" color="text.secondary">
            {KIND_LABELS[gap.kind]}
            {gap.context?.label ? ` · ${gap.value}` : ""}
          </Typography>
        </Stack>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (gap) => <Pill label={STATUS_LABELS[gap.status]} tone={STATUS_TONES[gap.status]} />,
    },
    {
      key: "note",
      header: "What is known",
      width: 320,
      cell: (gap) => (
        <Typography variant="body2" color="text.secondary">
          {gap.decision
            ? describeGapEntry(gap.decision)
            : (gap.escalationReason ?? "Not judged yet")}
        </Typography>
      ),
    },
    {
      key: "documents",
      header: "Documents",
      align: "right",
      cell: (gap) => <Typography variant="body2">{gap.documentsTotal}</Typography>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cardRole: "action",
      cell: (gap) => (
        <Stack direction="row" gap={1} justifyContent="flex-end">
          {gap.status !== "open" && <ReopenButton gap={gap} onReopen={reopen} />}
          <Button size="small" variant="outlined" onClick={() => decision.open(gap)}>
            {gap.status === "decided" ? "Change" : "Decide"}
          </Button>
        </Stack>
      ),
    },
  ];

  async function decide(gap: TaxonomyGapReview, entry: TaxonomyGapEntry) {
    await decideTaxonomyGap(gap.id, entry);
    await refresh();
  }

  return (
    <LibraryPage>
      <Stack gap={spacing.base}>
        <Stack direction={{ xs: "column", md: "row" }} gap={spacing.base}>
          <TextField
            select
            size="small"
            label="Status"
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value as TaxonomyGapFilters["status"] })
            }
            sx={{ minWidth: 150 }}
          >
            {GAP_STATUS_FILTERS.map((status) => (
              <MenuItem key={status} value={status}>
                {STATUS_LABELS[status]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Kind"
            value={filters.kind}
            onChange={(e) =>
              setFilters({ ...filters, kind: e.target.value as TaxonomyGapFilters["kind"] })
            }
            sx={{ minWidth: 180 }}
          >
            {Object.entries(KIND_LABELS).map(([kind, label]) => (
              <MenuItem key={kind} value={kind}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : loading ? (
          <LoadingState />
        ) : (
          <DataSequence
            columns={columns}
            rows={gaps}
            getRowId={(gap) => gap.id}
            emptyMessage="No gaps to settle."
            pagination={pagination}
            onRowClick={decision.open}
          />
        )}
      </Stack>

      {decision.value && (
        <DecideGapDialog
          key={decision.value.id}
          gap={decision.value}
          onClose={decision.close}
          onDecide={(entry) => decide(decision.value as TaxonomyGapReview, entry)}
        />
      )}
    </LibraryPage>
  );
}
